import {css, StyleSheet} from 'aphrodite';
import React, {useState, useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PolymerSubtype, POLYMER_SUBTYPE_TO_MODEL_TYPE} from 'constants/MolbioConstants';
import * as NavConstants from 'constants/NavConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {SchemasFilterFunction} from 'nav/sidepanel/listing/ItemListingUtil';
import {getStore} from 'redux-stores/Store';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {SearchUISelector} from 'search/reduceSearchUI';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getAppliedFiltersFromUserPreference} from 'search/SearchPreferences';
import {updateParamsAndSearch} from 'search/SearchUIReduxActions';
import {clearSearchParams} from 'search/SearchUtil';
import withSearching, {SearchProps} from 'search/withSearching';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

type BioentitySearchViewNavSection =
  | NavConstants.Section.ANTIBODY_NESTED_DATA_CREATION
  | NavConstants.Section.MOLBIO_BULK_ASSEMBLY
  | NavConstants.Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE
  | NavConstants.Section.MOD_PATTERNS
  | NavConstants.Section.MODEL_HUB_STRUCTURE_PREDICTION;

const SECTION_TO_ACTION_SOURCE: {[Section in BioentitySearchViewNavSection]: ActionOrigin} = {
  [NavConstants.Section.ANTIBODY_NESTED_DATA_CREATION]: ActionOrigin.ANTIBODY_NESTED_DATA_CREATION,
  [NavConstants.Section.MOLBIO_BULK_ASSEMBLY]: ActionOrigin.MOLBIO_BULK_ASSEMBLY,
  [NavConstants.Section.MOD_PATTERNS]: ActionOrigin.MODBIO_MOD_PATTERNS,
  [NavConstants.Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE]:
    ActionOrigin.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE,
  [NavConstants.Section.MODEL_HUB_STRUCTURE_PREDICTION]:
    ActionOrigin.MODEL_HUB_STRUCTURE_PREDICTION_COMPONENT_SEARCH,
};

// Props for a BioentitySearchView that specifically searches for polymer subtypes (eg sequences, oligos etc)
// The component converts the given polymer types to their corresponding model types
export interface PolymerSearchViewProps extends BaseBioentitySearchViewProps {
  polymerSubtypes: Array<PolymerSubtype>;
}

// Props for a generic BioentitySearchView that allows searching for any bioentity type
export interface BioentitySearchViewProps extends BaseBioentitySearchViewProps {
  availableTypes: Array<OligoAwareFolderItemType>;
}

// Props shared for any usage of BioentitySearchView
interface BaseBioentitySearchViewProps extends BaseBioentitySearchViewComponentProps {
  limitSelectedEntitiesParams?: {
    maxNumEntitiesToAdd: number;
    selectedEntityName: string;
  };
  initialFilters?: Array<Filter>;
}

// Props for the BioentitySearchView item listing
interface BioentitySearchViewListingProps extends BaseBioentitySearchViewComponentProps {
  availableTypes: Array<OligoAwareFolderItemType>;
  preservedFilterKeys: Array<FilterKey>;
}

// Props shared among all components that are part of the BioentitySearchView
interface BaseBioentitySearchViewComponentProps {
  navSection: BioentitySearchViewNavSection;
  rowSelectionMode: RowSelectionMode;
  selectedBioentityIds: Array<string>;
  setSelectedBioentityIds: (selectedIds: Array<string>) => void;
  alwaysShowSelector?: boolean;
  searchLimit?: number;
  filterSchemas?: SchemasFilterFunction;
}

function BioentitySearchViewListing(props: BioentitySearchViewListingProps & SearchProps): JSX.Element {
  const [isCompactDisplay, setIsCompactDisplay] = useState<boolean>(true);

  return (
    <ItemListing
      {...props}
      isCompactDisplay={isCompactDisplay}
      setIsCompactDisplay={setIsCompactDisplay}
      isExpanded={true}
      activeItemId={null}
      enableToggleExpanded={false}
      // Override the regular withSearching state management and control the selectedIds externally.
      selectedIds={props.selectedBioentityIds}
      onSelectIds={props.setSelectedBioentityIds}
      actionSource={SECTION_TO_ACTION_SOURCE[props.navSection]}
      availableTypes={props.availableTypes}
      makeCrumbs={undefined}
      extraPrimaryFilterOptions={[{filterKey: FilterKey.ANCESTOR_FOLDER}]}
      availableSortKeys={[
        SortKey.SORT_NAME_KEY,
        SortKey.SORT_IDENTIFIER_KEY,
        SortKey.SORT_MODIFIED_AT_KEY,
        SortKey.SORT_CREATED_AT_KEY,
      ]}
      renderToolbar={() => <></>}
      preservedFilterKeys={props.preservedFilterKeys ?? []}
      section={props.navSection}
      disableOpenItem={true}
      dataTestAttrs={{component: 'BioentitySearchView', element: 'root'}}
      disableBLAST={true}
      rowSelectionMode={props.rowSelectionMode}
      alwaysShowSelector={props.alwaysShowSelector}
    />
  );
}

function getDefaultSearchParams(
  availableTypes: Array<OligoAwareFolderItemType>,
  navSection: BioentitySearchViewNavSection,
  extraFilters?: Array<Filter>,
  searchLimit?: number
): SearchParams {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: availableTypes,
    },
    ...getAppliedFiltersFromUserPreference(navSection),
    ...(extraFilters ?? []),
  ];
  return {
    filters,
    offset: 0,
    limit: searchLimit ?? 25,
    query: '',
    sorts: [],
  };
}

/**
 * Wrapper around clearBioentitySearchViewSearchParams to clear any cached search params,
 * specifically for polymer search views.
 */
export function clearPolymerSearchViewSearchParams(
  polymerSubtypes: Array<PolymerSubtype>,
  navSection: BioentitySearchViewNavSection,
  initialFilters?: Array<Filter>
): void {
  const availableTypes = polymerSubtypes.map(
    (polymerSubtype) => POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]
  );
  clearBioentitySearchViewSearchParams(availableTypes, navSection, initialFilters);
}

/**
 * Helper to clear any cached search params associated with the search view.
 */
export function clearBioentitySearchViewSearchParams(
  availableTypes: Array<OligoAwareFolderItemType>,
  navSection: BioentitySearchViewNavSection,
  initialFilters?: Array<Filter>
): void {
  const store = getStore();
  const searchUISelector = new SearchUISelector(store.getState());
  const defaultSearchParams = getDefaultSearchParams(availableTypes, navSection, initialFilters);
  const existingSearchParams =
    searchUISelector.getSearchSessionState(navSection)?.searchParams ?? defaultSearchParams;

  const newParams = clearSearchParams(existingSearchParams, {
    preservedFilterKeys: [FilterKey.ANCESTOR_FOLDER],
    filtersToReset: [
      {
        ...keyToEmptyFilter(FilterKey.TYPES),
        value: availableTypes,
      },
      ...(initialFilters ?? []),
    ],
  });
  store.dispatch(
    updateParamsAndSearch(
      navSection,
      SearchType.FOLDERS_AND_ITEMS,
      newParams,
      getEmptyFilterErrors(newParams.filters)
    )
  );
}

/**
 * A search view for the given bioentity types.
 */
export default function BioentitySearchView({
  availableTypes,
  limitSelectedEntitiesParams,
  navSection,
  searchLimit,
  initialFilters,
  selectedBioentityIds,
  ...otherProps
}: BioentitySearchViewProps): JSX.Element {
  const preservedFilterKeys = useMemo(
    () => initialFilters?.filter((filter) => filter.locked).map((filter) => filter.key) ?? [],
    [initialFilters]
  );

  // Important to memoize because `withSearching` is an HOC that performs a query, so it will cause the React
  // tree to unmount everytime BioentitySearchView re-renders if we don't memoize this result.
  const SearchingComponent = useMemo(
    () =>
      withSearching(BioentitySearchViewListing, SearchType.FOLDERS_AND_ITEMS, navSection, {
        defaultSearchParams: getDefaultSearchParams(availableTypes, navSection, initialFilters, searchLimit),
        filterKeysToRemember: [FilterKey.ANCESTOR_FOLDER],
      }),
    [availableTypes, navSection, searchLimit, initialFilters]
  );

  return (
    <>
      {limitSelectedEntitiesParams != null &&
        selectedBioentityIds.length > limitSelectedEntitiesParams.maxNumEntitiesToAdd && (
          <div className={css(styles.banner)}>
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={`Adding all of the selected ${limitSelectedEntitiesParams.selectedEntityName} would exceed the maximum allowed number of ${limitSelectedEntitiesParams.selectedEntityName}. Remove ${selectedBioentityIds.length - limitSelectedEntitiesParams.maxNumEntitiesToAdd} ${pluralize(selectedBioentityIds.length - limitSelectedEntitiesParams.maxNumEntitiesToAdd, 'entity', 'entities')} to continue.`}
            />
          </div>
        )}
      <SearchingComponent
        availableTypes={availableTypes}
        searchLimit={searchLimit}
        navSection={navSection}
        selectedBioentityIds={selectedBioentityIds}
        preservedFilterKeys={preservedFilterKeys}
        {...otherProps}
      />
    </>
  );
}

/**
 * A wrapper around BioentitySearchView that specifically searches for polymers (sequences, oligos, etc)
 */
export function PolymerSearchView({polymerSubtypes, ...otherProps}: PolymerSearchViewProps): JSX.Element {
  const supportedTypes = useMemo(
    () => polymerSubtypes?.map((polymerSubtype) => POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]) ?? [],
    [polymerSubtypes]
  );
  return <BioentitySearchView {...otherProps} availableTypes={supportedTypes} />;
}

const styles = StyleSheet.create({
  banner: {
    padding: '15px',
  },
});
